import { PropsWithChildren, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { useToast } from '@givebutter/primitives/toast';
import EmojiPicker from '@emoji-mart/react';
import { faSmile } from '@fortawesome/pro-regular-svg-icons';
import { faArrowProgress } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UpdateWorkflowParams, updateWorkflow } from 'api/workflows';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { Workflow } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface EditWorkflowNameDialogProps extends PropsWithChildren {}

const EditWorkflowNameDialog = ({ children }: EditWorkflowNameDialogProps) => {
    const { error } = useToast();
    const closeRef = useRef<HTMLButtonElement>(null);
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const methods = Form.useGivebutterForm<UpdateWorkflowParams>({
        defaultValues: {
            name: workflow.name,
            icon: workflow.icon,
            trigger: workflow.trigger,
        },
    });
    const {
        control,
        formState: { isDirty },
        handleSubmit,
        watch,
        setValue,
        path,
        reset,
    } = methods;
    const icon = watch('icon');

    const { mutate: submit, isPending } = useMutation<
        Workflow,
        GivebutterAxiosError,
        UpdateWorkflowParams
    >({
        mutationFn: values => updateWorkflow(accountId, workflow.id, values),
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: workflowQueryKeys.workflows(accountId) });
            queryClient.setQueryData(
                workflowQueryKeys.workflowById(accountId, response.id),
                response,
            );
            closeRef.current?.click();
            reset({
                name: response.name,
                icon: response.icon,
                trigger: response.trigger,
            });
        },
        onError: e => {
            error(e.response?.data.message || 'Failed to update workflow. Please try again.');
        },
    });

    return (
        <Dialog.Root>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <div className="flex flex-col gap-4">
                        <Dialog.Header className="p-0">
                            <Avatar.Root className="!bg-gray-50">
                                <Avatar.Fallback aria-label={`Workflow icon`}>
                                    <FontAwesomeIcon
                                        className="text-gray-500"
                                        icon={faArrowProgress}
                                    />
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <Dialog.Close ref={closeRef} />
                        </Dialog.Header>
                        <Form.Provider {...methods}>
                            <form onSubmit={handleSubmit(values => submit(values))}>
                                <Dialog.Body>
                                    <div className="flex flex-col gap-8">
                                        <Dialog.Title className="sm:text-xl">
                                            Workflow information
                                        </Dialog.Title>

                                        <Form.Container>
                                            <Form.Label name={path('name')}>
                                                Name <Form.LabelAsterisk />
                                            </Form.Label>
                                            <div className="flex items-center gap-2">
                                                <Popover.Root>
                                                    <Popover.Trigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            color="neutral"
                                                            shape={'square'}
                                                            className="!border-gray-300"
                                                            aria-label="Edit"
                                                        >
                                                            {icon || (
                                                                <FontAwesomeIcon
                                                                    icon={faSmile}
                                                                    className="text-gray-400"
                                                                />
                                                            )}
                                                        </Button>
                                                    </Popover.Trigger>
                                                    <Popover.Portal>
                                                        <Popover.Content
                                                            side="bottom"
                                                            sideOffset={8}
                                                            align="start"
                                                            alignOffset={-16}
                                                        >
                                                            <EmojiPicker
                                                                theme="light"
                                                                onEmojiSelect={({
                                                                    native,
                                                                }: {
                                                                    native: string;
                                                                }) => {
                                                                    setValue('icon', native, {
                                                                        shouldDirty: true,
                                                                        shouldTouch: true,
                                                                        shouldValidate: true,
                                                                    });
                                                                }}
                                                            />
                                                        </Popover.Content>
                                                    </Popover.Portal>
                                                </Popover.Root>
                                                <Form.TextInputRoot
                                                    control={control}
                                                    name="name"
                                                    inputRootProps={{
                                                        className: 'flex-grow',
                                                    }}
                                                >
                                                    <Form.TextInputForm
                                                        autoComplete="off"
                                                        data-1p-ignore
                                                        autoFocus
                                                    />
                                                </Form.TextInputRoot>
                                            </div>
                                        </Form.Container>
                                        <div className="grid grid-cols-2 gap-4">
                                            <Dialog.Cancel asChild>
                                                <Button variant="outline" color="neutral">
                                                    Cancel
                                                </Button>
                                            </Dialog.Cancel>
                                            <Form.SubmitButton
                                                isSubmitting={isPending}
                                                disabled={!isDirty}
                                            >
                                                Save
                                            </Form.SubmitButton>
                                        </div>
                                    </div>
                                </Dialog.Body>
                            </form>
                        </Form.Provider>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const EditWorkflowNameDialogTrigger = Dialog.Trigger;
const Root = EditWorkflowNameDialog;
const Trigger = EditWorkflowNameDialogTrigger;

export {
    EditWorkflowNameDialog,
    EditWorkflowNameDialogTrigger,
    //
    Root,
    Trigger,
};

export type { EditWorkflowNameDialogProps };
